import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Users,
  X,
  CheckCircle,
  Calendar,
  Truck,
} from 'lucide-react'
import api from '../../services/api'
import PageHeader from '../../components/PageHeader'
import clsx from 'clsx'

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const getMilkBuyers = (ranchId, params = {}) =>
  api.get('/dairy/buyers', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

const createMilkBuyer = (data) =>
  api.post('/dairy/buyers', data).then(res => res.data)

const getMilkCollections = (ranchId, params = {}) =>
  api.get('/dairy/milk-collections', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

const createMilkCollection = (data) =>
  api.post('/dairy/milk-collections', data).then(res => res.data)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const buyerTypeLabels = {
  cooperative: 'Cooperative',
  processor: 'Processor',
  trader: 'Trader',
  direct: 'Direct',
}

const buyerTypeColors = {
  cooperative: 'bg-blue-100 text-blue-800',
  processor: 'bg-purple-100 text-purple-800',
  trader: 'bg-amber-100 text-amber-800',
  direct: 'bg-green-100 text-green-800',
}

const paymentStatusColors = {
  pending: 'bg-amber-100 text-amber-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  partial: 'bg-blue-100 text-blue-800',
}

const formatCurrency = (amount) => {
  if (amount == null) return '--'
  return new Intl.NumberFormat('en-UG', {
    style: 'currency',
    currency: 'UGX',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

// ---------------------------------------------------------------------------
// Add Buyer Modal
// ---------------------------------------------------------------------------

function AddBuyerModal({ ranchId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    ranch_id: ranchId,
    buyer_name: '',
    buyer_type: 'cooperative',
    phone: '',
    email: '',
    base_price_per_liter: '',
    quality_premium_pct: '',
    quality_penalty_pct: '',
    scc_threshold: '',
  })
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: createMilkBuyer,
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.detail || 'Failed to add buyer'),
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    if (!formData.buyer_name.trim()) {
      setError('Buyer name is required')
      return
    }
    const payload = {
      ...formData,
      base_price_per_liter: formData.base_price_per_liter ? parseFloat(formData.base_price_per_liter) : null,
      quality_premium_pct: formData.quality_premium_pct ? parseFloat(formData.quality_premium_pct) : null,
      quality_penalty_pct: formData.quality_penalty_pct ? parseFloat(formData.quality_penalty_pct) : null,
      scc_threshold: formData.scc_threshold ? parseInt(formData.scc_threshold) : null,
      phone: formData.phone || null,
      email: formData.email || null,
    }
    mutation.mutate(payload)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Add Buyer</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buyer Name *</label>
            <input
              type="text"
              required
              value={formData.buyer_name}
              onChange={(e) => setFormData({ ...formData, buyer_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="e.g. Jesa Farm Dairy"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buyer Type *</label>
            <select
              value={formData.buyer_type}
              onChange={(e) => setFormData({ ...formData, buyer_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="cooperative">Cooperative</option>
              <option value="processor">Processor</option>
              <option value="trader">Trader</option>
              <option value="direct">Direct</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="256XXXXXXXXX"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="buyer@example.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Base Price / Liter (UGX)</label>
            <input
              type="number"
              step="1"
              min="0"
              value={formData.base_price_per_liter}
              onChange={(e) => setFormData({ ...formData, base_price_per_liter: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="e.g. 1200"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Premium %</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={formData.quality_premium_pct}
                onChange={(e) => setFormData({ ...formData, quality_premium_pct: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Penalty %</label>
              <input
                type="number"
                step="0.1"
                min="0"
                value={formData.quality_penalty_pct}
                onChange={(e) => setFormData({ ...formData, quality_penalty_pct: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="10"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SCC Limit</label>
              <input
                type="number"
                min="0"
                value={formData.scc_threshold}
                onChange={(e) => setFormData({ ...formData, scc_threshold: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="400000"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              {mutation.isPending ? 'Saving...' : 'Add Buyer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Record Collection Modal
// ---------------------------------------------------------------------------

function RecordCollectionModal({ ranchId, buyers, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    ranch_id: ranchId,
    buyer_id: '',
    collection_date: new Date().toISOString().split('T')[0],
    volume_liters: '',
    quality_grade: 'grade_a',
    temperature_celsius: '',
    notes: '',
  })
  const [error, setError] = useState('')

  const selectedBuyer = buyers.find((b) => b.id === formData.buyer_id)
  const calculatedTotal =
    formData.volume_liters && selectedBuyer?.base_price_per_liter
      ? parseFloat(formData.volume_liters) * selectedBuyer.base_price_per_liter
      : null

  const mutation = useMutation({
    mutationFn: createMilkCollection,
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.detail || 'Failed to record collection'),
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    if (!formData.buyer_id) {
      setError('Please select a buyer')
      return
    }
    if (!formData.volume_liters || parseFloat(formData.volume_liters) <= 0) {
      setError('Volume must be greater than 0')
      return
    }

    const payload = {
      ...formData,
      volume_liters: parseFloat(formData.volume_liters),
      temperature_celsius: formData.temperature_celsius ? parseFloat(formData.temperature_celsius) : null,
      total_amount: calculatedTotal,
      notes: formData.notes || null,
    }
    mutation.mutate(payload)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Record Collection</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Buyer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buyer *</label>
            <select
              required
              value={formData.buyer_id}
              onChange={(e) => setFormData({ ...formData, buyer_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">Select buyer...</option>
              {buyers.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.buyer_name} ({buyerTypeLabels[b.buyer_type] || b.buyer_type})
                </option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Collection Date *</label>
            <input
              type="date"
              required
              value={formData.collection_date}
              onChange={(e) => setFormData({ ...formData, collection_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          {/* Volume + Grade */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Volume (L) *</label>
              <input
                type="number"
                step="0.1"
                min="0"
                required
                value={formData.volume_liters}
                onChange={(e) => setFormData({ ...formData, volume_liters: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="e.g. 120"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quality Grade</label>
              <select
                value={formData.quality_grade}
                onChange={(e) => setFormData({ ...formData, quality_grade: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="grade_a">Grade A</option>
                <option value="grade_b">Grade B</option>
                <option value="grade_c">Grade C</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          {/* Calculated total */}
          {calculatedTotal != null && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm text-emerald-700 font-medium">Estimated Total</span>
                <span className="text-lg font-bold text-emerald-800">{formatCurrency(calculatedTotal)}</span>
              </div>
              <p className="text-xs text-emerald-600 mt-1">
                {formData.volume_liters} L x {formatCurrency(selectedBuyer?.base_price_per_liter)} / L
              </p>
            </div>
          )}

          {/* Temperature */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temperature at Pickup (C)</label>
            <input
              type="number"
              step="0.1"
              value={formData.temperature_celsius}
              onChange={(e) => setFormData({ ...formData, temperature_celsius: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="e.g. 4.5"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Optional observations..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              {mutation.isPending ? 'Saving...' : 'Record Collection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function MilkBuyers({ ranchId }) {
  const queryClient = useQueryClient()
  const [showAddBuyerModal, setShowAddBuyerModal] = useState(false)
  const [showCollectionModal, setShowCollectionModal] = useState(false)
  const [collectionPage, setCollectionPage] = useState(1)
  const collectionLimit = 20

  const { data: buyersData, isLoading: buyersLoading } = useQuery({
    queryKey: ['milkBuyers', ranchId],
    queryFn: () => getMilkBuyers(ranchId),
  })

  const { data: collectionsData, isLoading: collectionsLoading } = useQuery({
    queryKey: ['milkCollections', ranchId, collectionPage],
    queryFn: () => getMilkCollections(ranchId, {
      limit: collectionLimit,
      offset: (collectionPage - 1) * collectionLimit,
    }),
  })

  const buyers = buyersData?.items || buyersData || []
  const collections = collectionsData?.items || collectionsData || []
  const collectionTotal = collectionsData?.total || collections.length
  const collectionPages = Math.ceil(collectionTotal / collectionLimit)

  const handleBuyerSuccess = () => {
    setShowAddBuyerModal(false)
    queryClient.invalidateQueries({ queryKey: ['milkBuyers', ranchId] })
  }

  const handleCollectionSuccess = () => {
    setShowCollectionModal(false)
    queryClient.invalidateQueries({ queryKey: ['milkCollections', ranchId] })
  }

  const isLoading = buyersLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Milk Buyers & Collections"
        subtitle="Manage buyers and track milk collections"
        icon={Users}
        actions={
          <>
            <button
              onClick={() => setShowCollectionModal(true)}
              className="btn bg-blue-600 text-white hover:bg-blue-700"
            >
              <Truck className="h-4 w-4 mr-2" />
              Record Collection
            </button>
            <button
              onClick={() => setShowAddBuyerModal(true)}
              className="btn bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Buyer
            </button>
          </>
        }
      />

      {/* Buyer List */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Buyers</h3>
        </div>

        {buyers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No buyers registered</h3>
            <p className="text-gray-500 mt-1">Add your milk buyers to start tracking collections</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Base Price/L</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Premium %</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {buyers.map((buyer) => (
                <tr key={buyer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{buyer.buyer_name}</div>
                    {buyer.phone && (
                      <div className="text-xs text-gray-500">{buyer.phone}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={clsx(
                      'px-2 py-1 text-xs font-medium rounded-full',
                      buyerTypeColors[buyer.buyer_type] || 'bg-gray-100 text-gray-800'
                    )}>
                      {buyerTypeLabels[buyer.buyer_type] || buyer.buyer_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                    {buyer.base_price_per_liter != null
                      ? formatCurrency(buyer.base_price_per_liter)
                      : '--'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                    {buyer.quality_premium_pct != null ? `${buyer.quality_premium_pct}%` : '--'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={clsx(
                      'px-2 py-1 text-xs font-medium rounded-full',
                      buyer.is_active !== false
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-500'
                    )}>
                      {buyer.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Collection History */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Collection History</h3>
        </div>

        {collectionsLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : collections.length === 0 ? (
          <div className="text-center py-12">
            <Truck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No collections recorded</h3>
            <p className="text-gray-500 mt-1">Record milk pickups by buyers</p>
          </div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Buyer</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Volume (L)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price/L</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {collections.map((col) => {
                  const gradeColors = {
                    grade_a: 'bg-green-100 text-green-800',
                    grade_b: 'bg-blue-100 text-blue-800',
                    grade_c: 'bg-amber-100 text-amber-800',
                    rejected: 'bg-red-100 text-red-800',
                  }
                  const gradeLabels = {
                    grade_a: 'A',
                    grade_b: 'B',
                    grade_c: 'C',
                    rejected: 'Rej',
                  }

                  return (
                    <tr key={col.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          {new Date(col.collection_date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {col.buyer_name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                        {Number(col.volume_liters).toFixed(1)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={clsx(
                          'px-2 py-1 text-xs font-medium rounded-full',
                          gradeColors[col.quality_grade] || 'bg-gray-100 text-gray-800'
                        )}>
                          {gradeLabels[col.quality_grade] || col.quality_grade}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                        {col.price_per_liter != null
                          ? formatCurrency(col.price_per_liter)
                          : '--'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        {col.total_amount != null
                          ? formatCurrency(col.total_amount)
                          : '--'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={clsx(
                          'px-2 py-1 text-xs font-medium rounded-full capitalize',
                          paymentStatusColors[col.payment_status] || 'bg-gray-100 text-gray-500'
                        )}>
                          {col.payment_status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {collectionPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Showing {((collectionPage - 1) * collectionLimit) + 1} to{' '}
                  {Math.min(collectionPage * collectionLimit, collectionTotal)} of {collectionTotal}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCollectionPage((p) => Math.max(1, p - 1))}
                    disabled={collectionPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCollectionPage((p) => Math.min(collectionPages, p + 1))}
                    disabled={collectionPage === collectionPages}
                    className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showAddBuyerModal && (
        <AddBuyerModal
          ranchId={ranchId}
          onClose={() => setShowAddBuyerModal(false)}
          onSuccess={handleBuyerSuccess}
        />
      )}
      {showCollectionModal && (
        <RecordCollectionModal
          ranchId={ranchId}
          buyers={buyers}
          onClose={() => setShowCollectionModal(false)}
          onSuccess={handleCollectionSuccess}
        />
      )}
    </div>
  )
}
